import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { prefersReducedMotion, throttle } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} buyItNowButton - The bar's only CTA
 * @property {HTMLElement} [quantityDisplay] - Read-only quantity indicator wrapper
 * @property {HTMLElement} [quantityNumber] - Read-only quantity number
 * @property {HTMLElement} [liveRegion] - Polite status region
 */

/** How long to wait for an add-to-cart to settle before giving up on the redirect. */
const CART_ADD_TIMEOUT_MS = 15000;

/** Fraction of a viewport to scroll before the scroll fallback reveals the bar. */
const SCROLL_FALLBACK_THRESHOLD = 0.6;

/**
 * A sticky add-to-cart bar that appears once the main buy buttons scroll out of view.
 *
 * The bar does not own a form. Its button forwards a click into the real product
 * form ("puppet click"), so quantity, selling plan, line-item properties and
 * gift-card recipient fields are all submitted without being duplicated here.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  // Only refs that always exist may be required — Component throws MissingRefError
  // otherwise, and the quantity refs disappear via Liquid branches.
  requiredRefs = ['stickyBar', 'buyItNowButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #checkoutPending = false;

  /** The exact button the puppet flag was set on, so it is cleared on that node. */
  /** @type {HTMLButtonElement | null} */
  #puppetTarget = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.#setupVisibility();

    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal,
    });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but if
    // the shopper scrolls before the Inbox bundle has upgraded <shopify-chat>,
    // the bar shows and nothing re-runs that check. Hide it once the element is
    // defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
  }

  // ---------------------------------------------------------------------------
  // Visibility
  // ---------------------------------------------------------------------------

  /**
   * Observes the main buy buttons and the footer. Every lookup here is a
   * best-effort candidate rather than a precondition: a missing element must
   * degrade the trigger, never disable the bar outright.
   */
  #setupVisibility() {
    const { signal } = this.#abortController;
    const trigger = this.#getTriggerElement();

    if (!trigger) {
      this.#setupScrollFallback(signal);
      return;
    }

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        // Only reveal once the trigger is ABOVE the viewport (scrolled past),
        // not while it is still below (not yet reached).
        const { top, bottom } = entry.target.getBoundingClientRect();
        if (bottom < 0 || top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hideStickyBar();
      }
    });
    this.#buyButtonsIntersectionObserver.observe(trigger);

    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]') ??
      document.querySelector('#shopify-section-footer');

    // Optional: without a footer the bar simply stays visible to the bottom.
    if (!footer) return;

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hideStickyBar();
          this.#hiddenByBottom = true;
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const { top, bottom } = trigger.getBoundingClientRect();
          if ((bottom < 0 || top < 0) && !this.#isChatActive()) this.#showStickyBar();
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );
    this.#mainBottomObserver.observe(footer);
  }

  /**
   * Last-resort trigger when no observable element resolves.
   * @param {AbortSignal} signal
   */
  #setupScrollFallback(signal) {
    const update = throttle(() => {
      const scrolledPast = window.scrollY > window.innerHeight * SCROLL_FALLBACK_THRESHOLD;
      if (scrolledPast && !this.#isStuck && !this.#isChatActive()) this.#showStickyBar();
      else if (!scrolledPast && this.#isStuck) this.#hideStickyBar();
    }, 100);

    window.addEventListener('scroll', update, { passive: true, signal });
    update();
  }

  /**
   * Resolves the element whose scroll position drives the bar, most specific first.
   * @returns {Element | null}
   */
  #getTriggerElement() {
    const productForm = this.#getProductForm();
    const section = this.closest('.shopify-section');

    const candidates = [
      productForm?.closest('.buy-buttons-block'),
      productForm,
      this.#addToCartTarget?.closest('add-to-cart-component'),
      section?.querySelector('.product-details'),
      section,
    ];

    return candidates.find((element) => element instanceof Element) ?? null;
  }

  #showStickyBar() {
    // Only one fixed bar may be visible at a time. A page can legitimately host
    // more than one product form — e.g. a featured product section rendered on
    // the product template — and two bars would stack on top of each other.
    for (const other of document.querySelectorAll('sticky-add-to-cart')) {
      if (other !== this && other instanceof StickyAddToCartComponent) other.hideForOtherInstance();
    }

    const { stickyBar } = this.refs;
    this.#isStuck = true;
    this.#hiddenByBottom = false;
    stickyBar.dataset.stuck = 'true';
    stickyBar.removeAttribute('inert');
  }

  /**
   * Hides this bar because another instance on the page is taking over.
   */
  hideForOtherInstance() {
    if (this.#isStuck) this.#hideStickyBar();
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';

    // Blur before going inert: engines disagree on what happens to focus that is
    // already inside an inert subtree, so move it deterministically.
    const active = document.activeElement;
    if (active instanceof HTMLElement && stickyBar.contains(active)) active.blur();

    stickyBar.setAttribute('inert', '');
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Adds the line via the real form, then goes to checkout once the cart settles.
   *
   * This is deliberately not a Shopify `payment_button` — only one may exist per
   * page and it lives in the main form — so there is no express wallet sheet here.
   *
   * @param {Event} [event]
   */
  handleBuyItNowClick = async (event) => {
    event?.preventDefault();
    if (this.#checkoutPending) return;

    const target = this.#resolveClickTarget();
    if (!target) return;

    const { buyItNowButton, liveRegion } = this.refs;

    this.#checkoutPending = true;
    buyItNowButton?.setAttribute('aria-busy', 'true');
    buyItNowButton?.setAttribute('disabled', '');

    // The drawer would slide open and then be thrown away by the navigation.
    const restoreCartDrawer = this.#suppressCartDrawerAutoOpen();

    // Arm before clicking: CartLinesUpdateEvent is dispatched synchronously,
    // before the fetch is issued.
    const settled = this.#waitForCartAdd(target);

    this.#markPuppet(target);
    target.click();

    const result = await settled;
    restoreCartDrawer();

    if (result.ok) {
      if (liveRegion) liveRegion.textContent = Theme.translations.added;
      // Stay in the pending state — the page is navigating away.
      window.location.assign(this.#checkoutUrl);
      return;
    }

    this.#checkoutPending = false;
    buyItNowButton?.removeAttribute('aria-busy');
    buyItNowButton?.removeAttribute('disabled');
    if (liveRegion && result.message) liveRegion.textContent = result.message;
  };

  /**
   * Resolves the real add-to-cart button, or null when the click must not proceed.
   *
   * A click on a `type="submit"` button inside an invalid form is swallowed by
   * native validation, so without this the sticky buttons would appear dead
   * whenever a required field (e.g. a gift-card recipient email) is off screen.
   *
   * @returns {HTMLButtonElement | null}
   */
  #resolveClickTarget() {
    const target = this.#addToCartTarget;
    if (!target || target.disabled) return null;

    const form = target.closest('form');
    if (form && !form.checkValidity()) {
      form.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      form.reportValidity();
      return null;
    }

    return target;
  }

  /**
   * Flags the real button so product-form.js skips its own add-to-cart animation.
   * @param {HTMLButtonElement} target
   */
  #markPuppet(target) {
    target.dataset.puppet = 'true';
    this.#puppetTarget = target;
  }

  /**
   * Temporarily stops the cart drawer from auto-opening.
   *
   * Safe because cart-drawer.js reads the `auto-open` attribute once, synchronously,
   * at dispatch time into a local const, and its promise handler re-reads that const
   * rather than the attribute.
   *
   * @returns {() => void} restore function
   */
  #suppressCartDrawerAutoOpen() {
    const drawers = Array.from(document.querySelectorAll('cart-drawer-component[auto-open]'));
    for (const drawer of drawers) drawer.removeAttribute('auto-open');
    return () => {
      for (const drawer of drawers) drawer.setAttribute('auto-open', '');
    };
  }

  /**
   * Resolves once the add-to-cart triggered by our puppet click has settled.
   *
   * @param {HTMLButtonElement} target - the real button, used to scope the events
   * @returns {Promise<{ ok: boolean, message?: string }>}
   */
  #waitForCartAdd(target) {
    const productForm = target.closest('product-form-component') ?? this.#getProductForm();

    return new Promise((resolve) => {
      const controller = new AbortController();
      let settled = false;

      /** @param {{ ok: boolean, message?: string }} result */
      const finish = (result) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        controller.abort();
        resolve(result);
      };

      const timer = setTimeout(() => finish({ ok: false }), CART_ADD_TIMEOUT_MS);
      const { signal } = controller;

      // A product page can host quick-add (recommendations), so only react to
      // events from our own form. Both events are dispatched on it and bubble.
      /** @param {Event} event */
      const isOurs = (event) =>
        Boolean(productForm && event.target instanceof Node && productForm.contains(event.target));

      document.addEventListener(
        StandardEvents.cartError,
        (/** @type {CartErrorEvent} */ event) => {
          if (!isOurs(event)) return;
          finish({ ok: false, message: event.detail?.description || event.error });
        },
        { signal }
      );

      document.addEventListener(
        StandardEvents.cartLinesUpdate,
        (/** @type {CartLinesUpdateEvent} */ event) => {
          if (event.action !== 'add' || !isOurs(event)) return;
          if (!(event.promise instanceof Promise)) return finish({ ok: true });

          event.promise
            .then(({ detail }) => finish({ ok: !detail?.didError, message: detail?.description }))
            .catch((error) => finish({ ok: false, message: error?.message }));
        },
        { signal }
      );
    });
  }

  // ---------------------------------------------------------------------------
  // Syncing
  // ---------------------------------------------------------------------------

  /**
   * Re-renders the bar from the server response when a variant is selected.
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { optionValueId } = event.detail ?? {};
    if (optionValueId) this.dataset.currentVariantId = optionValueId;

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, newProduct, resource: variant } = detail;

        // Combined listings swap the product entirely; adopt the new id rather
        // than bailing, otherwise the bar keeps the old title, image and price.
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (productId && productId !== this.dataset.productId) {
          return;
        }

        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        const newStickyBar = newStickyAddToCart?.querySelector('[ref="stickyBar"]');
        if (!newStickyAddToCart || !newStickyBar) return;

        const wasStuck = this.refs.stickyBar.dataset.stuck === 'true';

        morph(this.refs.stickyBar, newStickyBar, { ...MORPH_OPTIONS, childrenOnly: true });

        // Visibility is client state; the server always renders the bar hidden.
        this.refs.stickyBar.dataset.stuck = String(wasStuck);
        this.refs.stickyBar.toggleAttribute('inert', !wasStuck);
        this.dataset.variantAvailable = newStickyAddToCart.dataset.variantAvailable ?? 'false';

        if (variant?.id) this.dataset.currentVariantId = variant.id;
        if (variant == null) this.#handleVariantUnavailable();

        // Refs are refreshed by a MutationObserver, so they are one microtask
        // stale here — and the button node really is replaced at the
        // available/sold-out boundary, where the icon appears or disappears.
        await Promise.resolve();
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Falls back to the chosen option values when no variant matches the selection.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter(Boolean)
      .join(' / ');

    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
    variantTitleElement.removeAttribute('hidden');
  };

  /**
   * Clears the puppet flag once the cart operation settles — not when the event
   * is first dispatched, which happens before the request completes.
   * @param {CartLinesUpdateEvent | CartErrorEvent} event
   */
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      if (this.#puppetTarget) this.#puppetTarget.dataset.puppet = 'false';
      this.#puppetTarget = null;
    };

    // CartLinesUpdateEvent carries a promise; CartErrorEvent does not.
    if ('promise' in event && event.promise instanceof Promise) event.promise.finally(resetPuppet);
    else resetPuppet();
  };

  /**
   * Mirrors the main quantity selector. Read-only — the bar has no stepper.
   * @param {import('@theme/events').QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return; // cart drawer, not the product form
    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { buyItNowButton, quantityDisplay, quantityNumber } = this.refs;
    if (!quantityDisplay || !quantityNumber) return;

    quantityNumber.textContent = String(this.#currentQuantity);
    quantityDisplay.toggleAttribute('hidden', buyItNowButton.disabled || this.#currentQuantity <= 1);
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * The real add-to-cart button. Looked up fresh every time — a cached reference
   * goes stale whenever the buy buttons re-render.
   * @returns {HTMLButtonElement | null}
   */
  get #addToCartTarget() {
    return this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  /** @returns {string} */
  get #checkoutUrl() {
    return this.dataset.checkoutUrl || '/checkout';
  }

  /**
   * Whether Shopify Chat is live. The bar stays hidden when it is, to avoid
   * overlapping the chat launcher.
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /** @returns {HTMLElement | null} */
  #getProductForm() {
    const productId = this.dataset.productId;
    const sectionElement = this.closest('.shopify-section');
    if (!productId || !sectionElement) return null;

    return sectionElement.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
